import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface SheetProps {
  /** When true the sheet is shown (it stays mounted while closing so the slide runs). */
  open: boolean;
  /** Fires on Esc, scrim mousedown, or the close ×. */
  onClose: () => void;
  /** Mono eyebrow above the title (e.g. "Details", "Version history"). */
  kicker?: ReactNode;
  /** Header text shown beside the close ×. Omit for a header with just the × (e.g. when `header` supplies tabs). */
  title?: ReactNode;
  /** Muted sub-line under the title — dot-separated facets (type · project · count). */
  subtitle?: ReactNode;
  /** Extra fixed (non-scrolling) header region below the title row — InspectorTabs passes its <Tabs> strip here. */
  header?: ReactNode;
  /** Widen to `--inspector-w-wide` (the History drawer). */
  wide?: boolean;
  /** Accessible name on the dialog (defaults to "Sheet"). */
  ariaLabel?: string;
  /**
   * Test prefix. The panel gets `${dataTestId}-panel` (+ `data-open`), the
   * scrim `${dataTestId}-scrim`, and the close button `${dataTestId}-close`.
   */
  dataTestId?: string;
  /** ARIA role for the scrollable body (e.g. `tabpanel` for InspectorTabs). */
  bodyRole?: string;
  /** Test id for the scrollable body (e.g. `${dataTestId}-panel-<value>`). */
  bodyTestId?: string;
  /** The scrollable body content. */
  children: ReactNode;
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

/**
 * Right-anchored modal sheet — the single global "Details" surface primitive.
 * A dimmed scrim + a panel that slides in from the right edge, with a close ×,
 * an optional title/header region, and a scrollable body. Modal semantics:
 * focus-on-open, focus restore, Tab-cycle trap, Esc to close, scrim mousedown
 * to close.
 *
 * Portals to `document.body` so it overlays the whole viewport regardless of
 * the caller's layout. Always mounted while `open` toggles `data-open`, which
 * CSS targets to run the slide transition.
 *
 * A Modal or portaled popover opened from inside the sheet (History's
 * View/Diff/Restore via `.pn-modal-backdrop`, the UserPicker via
 * `.pn-popover-fixed`) renders ABOVE the scrim by z-index and portals outside
 * it, so interacting with them never reaches the scrim's dismiss handler.
 *
 * Composed by `InspectorTabs` (which passes the tab strip as `header`) and used
 * directly by surfaces that want a single titled panel (e.g. the project
 * settings sheet). CSS namespace: `pn-sheet` (-scrim / -panel / -head / -title
 * / -close / -header / -body).
 */
export function Sheet({
  open,
  onClose,
  kicker,
  title,
  subtitle,
  header,
  wide = false,
  ariaLabel = 'Sheet',
  dataTestId,
  bodyRole,
  bodyTestId,
  children
}: SheetProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Read onClose through a ref so the effect below depends only on `open` —
  // callers pass a fresh inline `onClose` each render, and re-running the effect
  // on every render would re-focus the panel and steal focus from tabs/inputs.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const tid = (suffix: string) =>
    dataTestId ? `${dataTestId}-${suffix}` : undefined;

  // Esc + focus management while open. Mirrors Modal: focus into the panel on
  // open, trap Tab within it, restore focus on close.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const node = panelRef.current;
    if (node) {
      const first = node.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? node).focus();
    }

    const onKey = (e: KeyboardEvent) => {
      // A Modal or portaled popover opened from inside the sheet owns the key
      // event — its own handler closes IT first; don't dismiss the whole sheet
      // or fight its focus trap.
      const modalOpen = Boolean(document.querySelector('.pn-modal-backdrop'));
      if (e.key === 'Escape') {
        if (modalOpen || document.querySelector('.pn-popover-fixed')) return;
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current || modalOpen) return;
      const focusables = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusables.length === 0) {
        e.preventDefault();
        panelRef.current.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !panelRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      const prev = previouslyFocused.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
    // onClose is read via onCloseRef — depend only on `open` so a fresh inline
    // onClose each render does not re-run this effect (which would steal focus).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return createPortal(
    <div
      className="pn-sheet-scrim"
      data-open={open ? 'true' : 'false'}
      data-testid={tid('scrim')}
      aria-hidden={open ? undefined : 'true'}
      onMouseDown={(e) => {
        // Only a click on the scrim itself (not a bubbled click from the panel)
        // dismisses. Modals/popovers opened from inside portal above the scrim.
        if (e.target === e.currentTarget) {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        className="pn-sheet-panel"
        data-open={open ? 'true' : 'false'}
        data-wide={wide ? 'true' : undefined}
        data-testid={tid('panel')}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-hidden={open ? undefined : 'true'}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="pn-sheet-head">
          {kicker || title || subtitle ? (
            <div className="pn-sheet-headings">
              {kicker ? <span className="pn-sheet-kicker">{kicker}</span> : null}
              {title ? <h2 className="pn-sheet-title">{title}</h2> : null}
              {subtitle ? (
                <div className="pn-sheet-sub">{subtitle}</div>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            className="pn-sheet-close"
            aria-label="Close"
            data-testid={tid('close')}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {header ? <div className="pn-sheet-header">{header}</div> : null}
        <div
          className="pn-sheet-body"
          role={bodyRole}
          data-testid={bodyTestId}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
